import React, { memo, useEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { TouchableOpacity } from 'react-native-gesture-handler';
import Animated from 'react-native-reanimated';

import RightIcon from '@/assets/icons/arrowPickBar';
import { ds } from '@/components/lib/design.system';
import { AnimatedPickbarContainer, AnimatedPickbarContainerProps } from '@/components/pickbar/AnimatedPickbarContainer';
import { usePreviousBetslip } from '@/feature/betslip-pickem/hooks/use-betslip-data';
import { useBetslipNavigation } from '@/feature/betslip-pickem/hooks/use-betslip-navigation';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { getMysteryPromoData } from '@/feature/betslip-pickem/hooks/use-fantasy-entry-promotions';
import { designSystem } from '@/styles/styles';
import { getLayoutAnimationsTransition } from '@/utils/animations/layout-animations-utils';
import { isWeb } from '@/utils/constants-platform-specific';
import { SCREEN_WIDTH } from '@gorhom/bottom-sheet';

import { PickbarMessage } from './PickbarMessage';
import { PickbarMultiplierInfo } from './PickbarMultiplierInfo';
import { PlayersHeadshotsList } from './PlayersHeadshotsList';

export const PICK_BAR_HEIGHT = 72;
export const PICK_BAR_ADDITIONAL_HEIGHT_FROM_MYSTERY = 24;
export const HEIGHT = 56;
export const HEIGHT_WITHOUT_BORDER = HEIGHT - 2;
export const MAX_HEADSHOTS = 4;
export const PICK_BAR_BOTTOM_DISTANCE = 16;

export type PickbarMessageMode = 'mystery' | 'sameTeam' | 'none';

/**
 * Fantasy pickbar content displaying player headshots, multiplier info, and optional messages.
 * Used with AnimatedPickbarContainer for animated entry/exit.
 */
const FantasyPickbarContents = () => {
    const [hasRendered, setHasRendered] = useState(false);
    const layoutTransition = getLayoutAnimationsTransition();

    useEffect(() => {
        setHasRendered(true);
    }, []);

    const { picks, allPicksFromSameTeam } = usePreviousBetslip();
    const { navigateToPickslip } = useBetslipNavigation();
    const hasMoreThanMaxHeadshots = picks.length > MAX_HEADSHOTS;

    const containerStyle = useMemo(() => {
        return hasMoreThanMaxHeadshots ? [styles.container, styles.containerWidth] : styles.container;
    }, [hasMoreThanMaxHeadshots]);

    const validationData = useBetslipStore(state => state.validationData);
    const mysteryPromo = getMysteryPromoData(picks, validationData.MYSTERY.betValidation);
    const mysteryPromoIsValid = (mysteryPromo?.errors?.length ?? 0) === 0;
    const showMysteryAvailable = mysteryPromo && mysteryPromoIsValid && picks.length > 0;
    const isMessageBoxDisplayed = allPicksFromSameTeam || showMysteryAvailable;

    let messageStatus: PickbarMessageMode = 'none';
    if (allPicksFromSameTeam) {
        messageStatus = 'sameTeam';
    } else if (showMysteryAvailable) {
        messageStatus = 'mystery';
    }

    return (
        <Animated.View layout={layoutTransition} testID={'fantasy-pickbar'}>
            <Animated.View
                style={[
                    styles.messageBoxContainer,
                    isMessageBoxDisplayed
                        ? [styles.messageBoxContainerExpanded, styles.shadow]
                        : styles.messageBoxContainerCollapsed,
                ]}
                layout={layoutTransition}
            >
                {isMessageBoxDisplayed ? <PickbarMessage mode={messageStatus} /> : null}
                <Animated.View
                    style={[containerStyle, !isMessageBoxDisplayed ? styles.shadow : null]}
                    layout={layoutTransition}
                >
                    <TouchableOpacity style={styles.contentWrapper} onPress={() => navigateToPickslip()}>
                        <Animated.View style={styles.content} layout={layoutTransition}>
                            <PlayersHeadshotsList hasRendered={hasRendered} />
                            <PickbarMultiplierInfo hasRendered={hasRendered} />
                        </Animated.View>

                        <Animated.View style={styles.rightIconContainer} layout={layoutTransition}>
                            <RightIcon />
                        </Animated.View>
                    </TouchableOpacity>
                </Animated.View>
            </Animated.View>
        </Animated.View>
    );
};

/**
 * Fantasy pickbar component that wraps FantasyPickbarContents with animated entry/exit
 */
export const FantasyPickbar = memo(
    ({ showContent, hiddenPosition, visiblePosition }: Omit<AnimatedPickbarContainerProps, 'children'>) => {
        return (
            <AnimatedPickbarContainer
                showContent={showContent}
                visiblePosition={visiblePosition}
                hiddenPosition={hiddenPosition}
            >
                <FantasyPickbarContents />
            </AnimatedPickbarContainer>
        );
    }
);

export const styles = StyleSheet.create({
    messageBoxContainer: {
        backgroundColor: ds.colorValue.background.neutral.subtler,
        borderColor: ds.colorValue.border.strong,
        borderBottomLeftRadius: 36,
        borderBottomRightRadius: 36,
        borderCurve: 'continuous',
    },
    messageBoxContainerExpanded: {
        paddingHorizontal: 8,
        paddingVertical: 7,
        borderWidth: 1,
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
        borderCurve: 'continuous',
    },
    messageBoxContainerCollapsed: {
        padding: 0,
        borderWidth: 0,
        borderTopLeftRadius: 36,
        borderTopRightRadius: 36,
        borderCurve: 'continuous',
    },
    container: {
        height: HEIGHT,
        backgroundColor: ds.colorValue.background.neutral.strong,
        borderWidth: 1,
        borderColor: ds.colorValue.border.strong,
        borderRadius: 28,
        alignSelf: 'center',
        borderCurve: 'continuous',
    },
    containerWidth: {
        width: isWeb ? '100%' : SCREEN_WIDTH - 32,
    },
    rightIconContainer: {
        width: 24,
        height: 24,
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        right: 12,
        top: '50%',
        marginTop: -12, // half of height to center vertically
    },
    contentWrapper: {
        borderCurve: 'continuous',
        borderRadius: 28,
        overflow: 'hidden',
        zIndex: 10,
        paddingLeft: 8,
        paddingRight: 12,
    },
    content: {
        height: HEIGHT_WITHOUT_BORDER,
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'row',
    },
    shadow: {
        shadowColor: designSystem.colors.neutralBlack,
        shadowOffset: {
            width: 0,
            height: 4,
        },
        shadowOpacity: 0.8,
        shadowRadius: 8,
        elevation: 16,
    },
});
